import type { CollectionTarget } from '../types/schedule.types';
import { shiftDateKeyMonths } from '../utils/dateKey';

/**
 * Pulls the END of the collection window forward by `leadMonths` whole months
 * by shifting every DATED collection target earlier, so the fund is fully
 * collected before the final service falls due (#727).
 *
 * Runs BETWEEN `buildCollectionTargets` and `alignCollectionTargets`, and does
 * exactly one thing: change dates. It deliberately does NOT clamp, collapse,
 * or derive a count — `alignCollectionTargets` (#730) already enforces every
 * measured elemis rule (period-1 floor B4, same-period collapse B5,
 * final-target-on-final-instalment B3) against whatever dates it is given, and
 * duplicating any of that here would create a second place for those rules to
 * drift.
 *
 * `leadMonths <= 0`, non-integer, or NaN returns the input array by the SAME
 * REFERENCE: identical content, zero allocation. That is the whole of what the
 * same-reference return buys — it is a purity property, NOT a mechanism that
 * suppresses any downstream memo, render or refetch. The caller consumes this
 * output inside the same `useMemo` that calls it and passes it straight into
 * `alignCollectionTargets`, which allocates fresh arrays on every call
 * regardless, so referential equality here prevents nothing downstream.
 *
 * The guarantee that actually protects every existing product in the unset case
 * is the BYTE-IDENTICAL elemis request, pinned at the `PaymentStep`
 * request-shape level (see the plan's D1). The `toBe` reference-equality
 * assertions in `collectionLead.test.ts` are a purity guard — they fail loudly
 * if this early return is ever "tidied" into an unconditional `map` — and must
 * not be read as evidence of memo suppression.
 */
export const applyCollectionLead = (
    targets: readonly CollectionTarget[],
    leadMonths: number,
): readonly CollectionTarget[] => {
    if (!Number.isInteger(leadMonths) || leadMonths <= 0) {
        return targets;
    }
    return targets.map((target) => {
        if (target.byDate === undefined) {
            // A `byPeriod` anchor cannot be month-shifted, and period 1 is
            // already the earliest slot elemis accepts (B4). Passing it
            // through untouched also preserves `alignCollectionTargets`'
            // `isSingleByPeriodFallback` decline for the all-undated basket,
            // keeping the #598 product-sign-off gate shut.
            return target;
        }
        const shifted = shiftDateKeyMonths(target.byDate, -leadMonths);
        // An unparsable key cannot arise from `buildCollectionTargets` (every
        // byDate it emits already passed `toDateKey`'s regex), but this takes
        // a public `readonly CollectionTarget[]`. Pass it through rather than
        // inventing a date: alignment then reads NaN from
        // `wholeMonthsBetween`, declines to derive a count, and elemis rejects
        // LOUDLY (Power of Ten rule 5 — never guess).
        return shifted === null ? target : { ...target, byDate: shifted };
    });
};
